import { useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import '@fontsource/material-icons/index.css'
import './dashboard_login.css'
import { GLORIFY_API_BASE_URL, glorifyApiClient } from '../services/api/glorify'

/**
 * Dashboard sign-in page — modelled on Material Design 3, dark baseline,
 * indigo-blue primary, teal tertiary.
 *
 * The "Sign in with Google" button redirects to the dashboard_backend OAuth
 * start endpoint at `${GLORIFY_API_BASE_URL}/auth/login`. The backend handles
 * the Google OAuth dance and redirects back with a session cookie.
 *
 * After consent, the backend redirects approved users to `/dashboard` and
 * pending users to `/dashboard/login?user_id=..&email=..&status=pending`.
 * Errors arrive at `/dashboard/login?status=error&message=oauth_failed`.
 *
 * Base URL resolution lives in src/services/api/glorify.ts — this page
 * imports it from there rather than duplicating the logic.
 */

const GOOGLE_LOGIN_URL = `${GLORIFY_API_BASE_URL}/auth/login`
const SUPPORT_EMAIL = 'hello@smetechfocus.com.au'

const DashboardLogin = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const status = searchParams.get('status')
  const pendingEmail = searchParams.get('email')
  const errorMessage = searchParams.get('message')

  // Quick session check — if already logged in, skip the login page entirely.
  useEffect(() => {
    glorifyApiClient
      .getSession()
      .then(() => {
        void navigate('/dashboard', { replace: true })
      })
      .catch(() => {
        // Not authenticated — stay on this page, nothing to do.
      })
  }, [navigate])

  return (
    <div className="dashboard-login">
      <main className="auth">

        {/* Brand lockup — links back to the umbrella landing page */}
        <Link to="/" className="brand" aria-label="SMETechFocus home">
          <span className="brand__mark">
            <span className="material-icons" aria-hidden="true">hub</span>
          </span>
          <span className="brand__text">
            <b>SMETechFocus</b>
            <span>Partners Portal</span>
          </span>
        </Link>

        {/* OAuth status banners */}
        {status === 'pending' && (
          <div className="dl-banner dl-banner--info" role="alert">
            <span className="material-icons" aria-hidden="true">hourglass_top</span>
            <div>
              <b>Account awaiting approval</b>
              <p>
                {pendingEmail
                  ? `Your account (${pendingEmail}) has been registered.`
                  : 'Your account has been registered.'}{' '}
                An admin will review and approve your access shortly.
              </p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="dl-banner dl-banner--error" role="alert">
            <span className="material-icons" aria-hidden="true">error_outline</span>
            <div>
              <b>Sign-in failed</b>
              <p>
                {errorMessage
                  ? decodeURIComponent(errorMessage)
                  : 'An error occurred during Google sign-in. Please try again.'}
              </p>
            </div>
          </div>
        )}

        {/* Sign-in card */}
        <section className="card">
          <div className="card__head">
            <h1 className="md-headline-small">Welcome back</h1>
            <p className="md-body-medium">Sign in with Google to access your dashboard.</p>
          </div>

          {/*
            Uses a plain <a> rather than <Link> because this redirects out to
            the dashboard_backend OAuth start endpoint — leaving the SPA.
            rel="noopener" is omitted intentionally: this IS the auth flow,
            not a third-party link, and the backend needs the same-origin
            referrer to validate the request.
          */}
          <a
            className="google-btn"
            href={GOOGLE_LOGIN_URL}
            aria-label="Sign in with Google"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Sign in with Google
          </a>

          {/* Approval note */}
          <div className="note">
            <span className="material-icons" aria-hidden="true">info</span>
            <div className="md-body-medium">
              <b>Authentication with Google</b>
              <p>
                New accounts need admin approval before dashboard access.
                You'll be notified once your account is approved.
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="divider-row" aria-hidden="true">
            <hr className="md-divider" />
            <span>Need a hand</span>
            <hr className="md-divider" />
          </div>

          {/* Support line */}
          <p className="support md-body-medium">
            Questions or access issues?{' '}
            <a href={`mailto:${SUPPORT_EMAIL}`}>Contact support</a>
          </p>
        </section>

        {/* Back-to-home link */}
        <div className="back-wrap">
          <Link to="/" className="back">
            <span className="material-icons" aria-hidden="true">arrow_back</span>
            Back to SMETechFocus
          </Link>
        </div>
      </main>
    </div>
  )
}

export default DashboardLogin
